import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Descriptions,
  Drawer,
  Image,
  Input,
  message,
  Modal,
  Row,
  Space,
  Spin,
  Tag,
  Timeline,
  Upload,
} from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  PlayCircleOutlined,
  SendOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { rectificationService } from '../services';
import {
  RECTIFICATION_SEVERITY_MAP,
  RECTIFICATION_SOURCE_MAP,
  RECTIFICATION_STATUS_MAP,
  RectificationStatus,
  type IRectification,
} from '../types';

interface RectificationDetailDrawerProps {
  open: boolean;
  rectificationId?: string;
  onClose: () => void;
  onSuccess?: () => void;
}

const RectificationDetailDrawer: React.FC<RectificationDetailDrawerProps> = ({ open, rectificationId, onClose, onSuccess }) => {
  const [rectification, setRectification] = useState<IRectification | null>(null);
  const [loading, setLoading] = useState(false);
  const [actionModalVisible, setActionModalVisible] = useState(false);
  const [currentAction, setCurrentAction] = useState<string>('');
  const [comment, setComment] = useState('');

  const fetchRectification = async () => {
    if (!rectificationId) {
      setRectification(null);
      return;
    }

    try {
      setLoading(true);
      const response = await rectificationService.getById(rectificationId);
      if (response.success && response.data) {
        setRectification(response.data);
      } else {
        setRectification(null);
      }
    } catch (error: any) {
      message.error(error.message || '获取整改详情失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && rectificationId) {
      fetchRectification();
    }
  }, [open, rectificationId]);

  const openActionModal = (action: string) => {
    setCurrentAction(action);
    setActionModalVisible(true);
  };

  const handleAction = async () => {
    if (!rectificationId) return;

    try {
      let response;
      switch (currentAction) {
        case 'start':
          response = await rectificationService.start(rectificationId);
          break;
        case 'submit':
          response = await rectificationService.submit(rectificationId, { comment });
          break;
        case 'director_accept':
          response = await rectificationService.directorAccept(rectificationId, { accepted: true, comment });
          break;
        case 'director_reject':
          response = await rectificationService.directorAccept(rectificationId, { accepted: false, comment });
          break;
        case 'owner_accept':
          response = await rectificationService.ownerAccept(rectificationId, { accepted: true, comment });
          break;
        case 'owner_reject':
          response = await rectificationService.ownerAccept(rectificationId, { accepted: false, comment });
          break;
      }

      if (response?.success) {
        message.success('操作成功');
        setActionModalVisible(false);
        setComment('');
        await fetchRectification();
        onSuccess?.();
      }
    } catch (error: any) {
      message.error(error.message || '操作失败');
    }
  };

  const renderActionButtons = () => {
    if (!rectification) return null;

    switch (rectification.status) {
      case RectificationStatus.PENDING:
      case RectificationStatus.NOTIFIED:
        return <Button type="primary" icon={<PlayCircleOutlined />} onClick={() => openActionModal('start')}>开始处理</Button>;
      case RectificationStatus.IN_PROGRESS:
        return <Button type="primary" icon={<SendOutlined />} onClick={() => openActionModal('submit')}>提交整改</Button>;
      case RectificationStatus.SUBMITTED:
        return (
          <Space>
            <Button type="primary" icon={<CheckOutlined />} onClick={() => openActionModal('director_accept')}>总监验收通过</Button>
            <Button danger icon={<CloseOutlined />} onClick={() => openActionModal('director_reject')}>总监验收不通过</Button>
          </Space>
        );
      case RectificationStatus.DIRECTOR_ACCEPTED:
        return (
          <Space>
            <Button type="primary" icon={<CheckOutlined />} onClick={() => openActionModal('owner_accept')}>业主验收通过</Button>
            <Button danger icon={<CloseOutlined />} onClick={() => openActionModal('owner_reject')}>业主验收不通过</Button>
          </Space>
        );
      default:
        return null;
    }
  };

  const statusInfo = rectification ? RECTIFICATION_STATUS_MAP[rectification.status] : undefined;
  const severityInfo = rectification ? RECTIFICATION_SEVERITY_MAP[rectification.severity] : undefined;

  return (
    <>
      <Drawer
        title={rectification ? `整改详情 - ${rectification.rectificationNo}` : '整改详情'}
        open={open}
        onClose={onClose}
        width={980}
        destroyOnClose={false}
        extra={renderActionButtons()}
      >
        {loading ? (
          <div style={{ padding: 48, textAlign: 'center' }}><Spin size="large" /></div>
        ) : !rectification ? (
          <div style={{ padding: 24, textAlign: 'center' }}>整改不存在</div>
        ) : (
          <>
            <Card bordered={false} bodyStyle={{ padding: 0 }}>
              <Descriptions bordered column={2} size="small">
                <Descriptions.Item label="整改编号">{rectification.rectificationNo}</Descriptions.Item>
                <Descriptions.Item label="整改标题">{rectification.title}</Descriptions.Item>
                <Descriptions.Item label="状态"><Tag color={statusInfo?.color}>{statusInfo?.label}</Tag></Descriptions.Item>
                <Descriptions.Item label="严重程度"><Tag color={severityInfo?.color}>{severityInfo?.label}</Tag></Descriptions.Item>
                <Descriptions.Item label="整改来源">{RECTIFICATION_SOURCE_MAP[rectification.source]}</Descriptions.Item>
                <Descriptions.Item label="扣除比例">{rectification.deductionPercentage}%</Descriptions.Item>
                <Descriptions.Item label="扣除工程量">{rectification.deductedQuantity?.toLocaleString() || 0}</Descriptions.Item>
                <Descriptions.Item label="扣除金额">¥{rectification.deductedAmount?.toLocaleString() || 0}</Descriptions.Item>
                <Descriptions.Item label="原进度">{rectification.originalProgress}%</Descriptions.Item>
                <Descriptions.Item label="恢复后进度">{rectification.progressRestored ? `${rectification.restoredProgress}%` : '-'}</Descriptions.Item>
                <Descriptions.Item label="责任人">{rectification.responsibleUserName || '-'}</Descriptions.Item>
                <Descriptions.Item label="责任单位">{rectification.responsibleParty || '-'}</Descriptions.Item>
                <Descriptions.Item label="整改期限">{rectification.deadline ? new Date(rectification.deadline).toLocaleDateString() : '-'}</Descriptions.Item>
                <Descriptions.Item label="处罚金额">¥{rectification.penaltyAmount?.toLocaleString() || 0}</Descriptions.Item>
                <Descriptions.Item label="问题描述" span={2}>{rectification.description}</Descriptions.Item>
              </Descriptions>
            </Card>

            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col span={12}>
                <Card title="问题照片" size="small">
                  <Image.PreviewGroup>
                    <Space wrap>
                      {rectification.issueAttachments?.length ? rectification.issueAttachments.map((att, index) => (
                        <Image key={index} width={100} height={100} src={att.url} style={{ objectFit: 'cover' }} />
                      )) : <span style={{ color: '#999' }}>暂无照片</span>}
                    </Space>
                  </Image.PreviewGroup>
                </Card>
              </Col>
              <Col span={12}>
                <Card title="整改后照片" size="small">
                  <Image.PreviewGroup>
                    <Space wrap>
                      {rectification.rectificationAttachments?.length ? rectification.rectificationAttachments.map((att, index) => (
                        <Image key={index} width={100} height={100} src={att.url} style={{ objectFit: 'cover' }} />
                      )) : <span style={{ color: '#999' }}>暂无照片</span>}
                    </Space>
                  </Image.PreviewGroup>
                </Card>
              </Col>
            </Row>

            <Card title="处理过程" style={{ marginTop: 16 }}>
              <Timeline>
                {rectification.processHistory.map((history, index) => (
                  <Timeline.Item key={index}>
                    <p><strong>{history.userName}</strong> - {history.action}</p>
                    {history.comment && <p>备注: {history.comment}</p>}
                    <p style={{ color: '#999' }}>{new Date(history.timestamp).toLocaleString()}</p>
                  </Timeline.Item>
                ))}
              </Timeline>
            </Card>
          </>
        )}
      </Drawer>

      <Modal
        title="操作确认"
        open={actionModalVisible}
        onCancel={() => {
          setActionModalVisible(false);
          setComment('');
        }}
        onOk={handleAction}
      >
        <Input.TextArea rows={4} placeholder="请输入备注信息" value={comment} onChange={(e) => setComment(e.target.value)} />
        {currentAction === 'submit' && (
          <div style={{ marginTop: 16 }}>
            <Upload>
              <Button icon={<UploadOutlined />}>上传整改后照片</Button>
            </Upload>
          </div>
        )}
      </Modal>
    </>
  );
};

export default RectificationDetailDrawer;
